import axios, { AxiosRequestConfig, AxiosResponse } from "axios";

export enum Methods {
	GET = "GET",
	POST = "POST",
	PUT = "PUT",
	PATCH = "PATCH",
	DELETE = "DELETE",
}

export default class LicenseClient {

	private readonly baseUrl = process.env.https://api.coderlicences.com;
	private auth?: string;
	private identifier?: string;

	constructor(auth?: string, identifier?: string) {
		this.auth = auth;
		this.identifier = identifier;
	}

	setAuth(auth?: string) {
		this.auth = auth;
	}

	setIdentifier(identifier?: string) {
		this.identifier = identifier;
	}

	private async request<T>(path: string, method: Methods, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
		return await axios.create({
			baseURL: this.baseUrl,
			headers: {
				'authorization': this.auth ? ('Bearer ' + this.auth) : undefined,
				'identifier': this.identifier,
			},
			validateStatus: function (status) {
				return status < 300;
			},
		}).request<T>({
			url: path,
			method: method,
			...config,
		});
	}

	private get<T>(path: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
		return this.request<T>(path, Methods.GET, config);
	}

	private post<T>(path: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
		return this.request<T>(path, Methods.POST, config);
	}

	private put<T>(path: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
		return this.request<T>(path, Methods.PUT, config);
	}

	private patch<T>(path: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
		return this.request<T>(path, Methods.PATCH, config);
	}

	private delete<T>(path: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
		return this.request<T>(path, Methods.DELETE, config);
	}

	getLoggedLicense() {
		return this.get<ILicenseSession>("/api/v1/license/auth/user");
	}

	getWhitelabel() {
		return this.get<IWhitelabel>(`/api/v1/license/auth/config`);
	}

	forgotPassword(data: { email: string }) {
		return this.post<IPasswordReset>("/api/v1/license/password-reset", { data });
	}

	login(data: LicenseLoginRequest) {
		return this.post<ILicenseSession>("/api/v1/license/auth", { data });
	}

	register(data: any) {
		return this.post<ILicenseSession>("/api/v1/license/register", { data });
	}

	validatePasswordReset(token: string) {
		return this.get<null>(`/api/v1/license/password-reset/${token}`);
	}

	resetAgentPassword(token: string, data: { password: string }) {
		return this.post<null>(`/api/v1/license/password-reset/${token}`, { data });
	}

	getCoupon(code: string) {
		return this.get<ICoupon>(`/api/v1/license/coupon/${code}`);
	}

	listSubscriptions() {
		return this.get<ILicenseSubscription[]>("/api/v1/license/subscription");
  }

	listInvoices() {
		return this.get<ILicenseInvoice[]>("/api/v1/license/subscription/invoices");
  }

	getTranscriptionUsage() {
		return this.get<TranscriptionUsageResponse>(`/api/v1/license/transcription/usage`);
	}

	getStates() {
		return this.get<IState[]>("/api/v1/state");
	}

	getCities(id: string | number) {
		return this.get<ICity[]>(`/api/v1/state/${id}`);
	}

	getPlans() {
		return this.get<IWhitelabelPlan[]>(`/api/v1/license/plan`);
	}

	getPaymentMethods() {
		return this.get<PaymentMethod[]>(`/api/v1/license/plan/payment-methods`);
	}

	createSubscription(data: CreateSubscriptionRequest) {
		return this.post<string>("/api/v1/license/subscription", { data });
  }

	cancelSubscription(id: number) {
		return this.delete<string>(`/api/v1/license/subscription/${id}`);
	}

}
